import { useEffect, useState, type ReactNode } from 'react';
import { checkNativeVersionGate, type VersionGateResult } from '../nativeVersionGate';
import { Button, Card } from './ui';

type GateState = 'checking' | 'ok' | VersionGateResult;

/**
 * Höchstens so lange wartet die Oberfläche auf die Versionsprüfung. Hängt die Anfrage (schlechtes Mobilnetz, Server
 * antwortet nicht), bliebe sonst der Bildschirm dauerhaft weiß, denn während der Prüfung wird nichts angezeigt.
 */
export const GATE_TIMEOUT_MS = 4000;

/**
 * Versions-Sperre der nativen App (P5): Blockiert die Oberfläche, solange die installierte App-Version unter der
 * vom Server vorgegebenen Mindestversion liegt. In einem gewöhnlichen Browser oder ohne gesetzte Mindestversion
 * geschieht nichts (siehe `checkNativeVersionGate`). Schlägt die Prüfung selbst fehl (kein Netz, Server nicht
 * erreichbar) oder dauert sie länger als `GATE_TIMEOUT_MS`, wird NICHT gesperrt: Die Offline-Kasse darf nie an
 * einer Versionsprüfung scheitern. Ein spätes Ergebnis wird dann verworfen (keine Sperre mitten im Verkauf), der
 * nächste App-Start prüft erneut.
 */
export function UpdateRequired({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>('checking');

  useEffect(() => {
    let settled = false;
    const settle = (next: GateState): void => {
      if (settled) return;
      settled = true;
      setState(next);
    };
    const timer = setTimeout(() => {
      settle('ok');
    }, GATE_TIMEOUT_MS);
    checkNativeVersionGate().then(
      (result) => {
        settle(result ?? 'ok');
      },
      () => {
        settle('ok');
      },
    );
    return () => {
      settled = true;
      clearTimeout(timer);
    };
  }, []);

  if (state === 'checking') return null;
  if (state === 'ok') return <>{children}</>;

  return (
    <div className="narrow stack stack--lg" style={{ paddingTop: '20vh' }}>
      <Card>
        <div className="stack">
          <h1>Bitte aktualisieren</h1>
          <p>
            Diese Version von BARSO ({state.currentVersion}) wird nicht mehr unterstützt. Bitte
            laden Sie die neue Version ({state.minVersion} oder neuer) aus dem Store.
          </p>
          {state.storeUrl !== null ? (
            <Button
              icon="sync"
              onClick={() => {
                window.location.href = state.storeUrl ?? '';
              }}
            >
              Jetzt aktualisieren
            </Button>
          ) : (
            <p className="muted">Bitte öffnen Sie den App Store und suchen Sie nach „BARSO“.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
